import _ from "lodash";
/** External */
import React, { useState } from "react";
import { connect } from "react-redux";

/** Internal */
import { useI18n } from "Hooks";
import { Modal, Button } from "@toddle-edu/ds-web";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { compose, graphql } from "lib/apolloClient";

/* Relative **/
import classes from "./FamilyInviteChildSelectionV2.scss";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getChildrenQuery } from "modules/CommonQuery";
import { getChildrenFromCache } from "modules/CommonGraphqlHelpers";
import StudentContent from "./components/StudentContent";
import { markStudentAsRequestedForFamilyInvite } from "Courses/modules/CoursesModule";

const FamilyInviteChildSelectionV2 = props => {
  const {
    onClose,
    allChildren,
    courseId,
    markStudentAsRequestedForFamilyInvite,
    learningCourseId,
  } = props;

  const { t } = useI18n(["common"]);

  const [isLoading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchText, setSearchText] = useState("");

  const handleSelectionChange = id => setSelectedStudent(id);

  const handleSubmit = async () => {
    setLoading(true);
    await markStudentAsRequestedForFamilyInvite({
      courseId,
      childIds: [selectedStudent?.id],
      learningCourseId,
    });
    setLoading(false);
    onClose();
  };

  return (
    <>
      <Modal
        dsVersion="2.0"
        width="480px"
        height="600px"
        isOpen={true}
        onClose={onClose}
      >
        <Modal.Header
          isClosable
          title={t("common:request_access_to_your_child_account")}
        ></Modal.Header>
        <Modal.Body>
          <StudentContent
            handleSelectionChange={handleSelectionChange}
            selectedStudent={selectedStudent}
            courseId={courseId}
            allChildren={allChildren}
            searchText={searchText}
            setSearchText={setSearchText}
            onClose={onClose}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.buttonContainer}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              onClick={onClose}
            >
              {t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              disabled={selectedStudent === null || isLoading}
              variant="progressive"
              size="medium"
              onClick={handleSubmit}
            >
              {t("common:request_access")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {isLoading && <FullScreenLoader />}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const courseId = ownProps.courseId;
  const userId = userInfo.id;
  const childrenDataPresent = ownProps.childrenDataPresent;
  const learningCourseId = _.get(
    state,
    "login.invitedFamilyData.metadata.learningCourseId",
    ""
  );

  return {
    courseId,
    userId,
    isData: true,
    isLoading: false,
    childrenDataPresent,
    learningCourseId,
  };
};

const mapActionCreators = { markStudentAsRequestedForFamilyInvite };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getChildrenQuery, {
    name: "getChildren",
    options: ({ userId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: "FAMILY_MEMBER",
      },
    }),
    skip: ({ childrenDataPresent }) => childrenDataPresent,
    props({ getChildren, ownProps: { userId, isData, isLoading } }) {
      const childrenData = getChildrenFromCache({
        id: userId,
        type: "FAMILY_MEMBER",
      });
      const children = _.get(childrenData, "children", []);

      return {
        isData: !_.isEmpty(childrenData) && isData,
        isLoading:
          _.includes([1, 2], getChildren["networkStatus"]) || isLoading,
        allChildren: children,
      };
    },
  }),
  withLoader
)(FamilyInviteChildSelectionV2);
